import {
  AbsoluteTimeRange,
  DURATION_UNIT,
  DurationString,
  formatWithTimeZone,
  getDateTimeFormat,
  getDurationFromTimeRangeStart,
  getOffsetStrFromTimeZoneName,
  getTimeRangeFromDurationString,
  isLocalTimeZone,
  isRelativeTimeRange,
  isTimeRangeInputValueAbsolute,
  normalizeTimeZone,
  parseDurationString,
  TimeFormat,
  TimeRangeInputValue,
  TimeRangeValue,
} from '@chronosphereio/core';
import { parse as parseDate } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { DATE_TIME_FORMAT_12, DATE_TIME_FORMAT_12_SHORT_TIME, isValidDate } from '@/core';

/**
 * Format start and end time based on provided date format
 */
export function formatAbsoluteRange(timeRange: AbsoluteTimeRange, dateFormat: string, timeZone?: string) {
  const formattedStart = formatWithTimeZone(timeRange.start, dateFormat, timeZone);
  const formattedEnd = formatWithTimeZone(timeRange.end, dateFormat, timeZone);

  return `${formattedStart} - ${formattedEnd}`;
}

/**
 * Turns a duration string into a human readable time range
 * e.g. '25m' becomes 'Last 25 minutes'
 */
export function formatRelativeRange(pastDuration: DurationString) {
  const parsedDuration = parseDurationString(pastDuration);

  let formattedValue = 'Last ';
  for (const [key, value] of Object.entries(parsedDuration)) {
    if (value !== 0) {
      const timeUnit = value === 1 ? key.slice(0, -1) : key; // make time unit singular if value is 1
      formattedValue += `${value} ${timeUnit} `;
    }
  }

  return formattedValue.trim();
}

type formatTimeRangeArgs = {
  timeRange: TimeRangeValue;
  timeFormat: TimeFormat;
  timeZone?: string;
  omitSeconds?: boolean;
};

export const formatTimeRange = ({ timeRange, timeFormat, timeZone, omitSeconds }: formatTimeRangeArgs) => {
  const dateFormat = getDateTimeFormat({ timeFormat, omitSeconds });

  const formattedValue = isRelativeTimeRange(timeRange)
    ? formatRelativeRange(timeRange.pastDuration)
    : formatAbsoluteRange(timeRange, dateFormat, timeZone);
  return formattedValue;
};

export interface FormatTimeRangeInputValueArgs {
  timeRange: TimeRangeInputValue;
  timeFormat: TimeFormat;
  timeZone?: string;
  omitSeconds?: boolean;
}

export function formatTimeRangeInputValue({
  timeRange,
  timeFormat,
  timeZone,
  omitSeconds,
}: FormatTimeRangeInputValueArgs) {
  const dateFormat = getDateTimeFormat({ timeFormat, omitSeconds });

  if (isTimeRangeInputValueAbsolute(timeRange)) {
    return formatAbsoluteRange(timeRange, dateFormat, timeZone);
  }

  const pastDuration = getDurationFromTimeRangeStart(timeRange);
  if (pastDuration !== undefined) {
    return formatRelativeRange(pastDuration);
  }

  // We got some invalid input value, so just return an empty string rather than throwing
  return '';
}

/**
 * Returns true if both the start/end portions of an absolute time range end in 00 seconds.
 * Used to determine whether or not to show seconds in the date time picker.
 */
export function timeRangeHasZeroSeconds({ start, end }: AbsoluteTimeRange): boolean {
  return start.getSeconds() === 0 && end.getSeconds() === 0;
}

/**
 * Returns true if both the start/end portions of an absolute time range are valid dates.
 */
export const absoluteTimeRangeHasValidDates = ({ start, end }: AbsoluteTimeRange): boolean => {
  return isValidDate(start) && isValidDate(end);
};

export const absoluteTimeRangeDatesHaveCorrectOrder = ({ start, end }: AbsoluteTimeRange): boolean => {
  return end.getTime() > start.getTime();
};

/**
 * Takes a time that has been parsed from a string (and therefore is currently in local time) and
 * adjust it to instead be that time in the specified time zone.
 */
const parsedTimeToZonedTime = (date: Date, timeZone?: string): Date => {
  const normalizedTimeZone = normalizeTimeZone(timeZone);
  const localTimeZone = isLocalTimeZone(normalizedTimeZone);

  // Note: the date-fns method `zonedTimeToUtc` is misleadingly named (they renamed it to `toZonedTime`
  // in a recent major). It formats into local time in the specified time zone.
  return localTimeZone ? date : zonedTimeToUtc(date, normalizedTimeZone);
};

/**
 * Parses a non-ISO absolute date in the specified time zone.
 */
const parseAbsoluteDate = (dateStr: string, format: string, timeZone?: string) => {
  const referenceDate = new Date();
  return parsedTimeToZonedTime(parseDate(dateStr.trim(), format, referenceDate), timeZone);
};

// Rough regex that checks for the V1 Chronosphere date format enough to avoid some edge cases
// where someone enters an invalid date input that is still parseable to our parsing library and
// leads to weird results (e.g. entering a date with a 2-digit year instead of 4-digit). We still
// leave the majority of date parsing to our date parsing lib.
const ROUGH_DATE_REGEX_STR_V1 = '\\d{2}\\/\\d{2}\\/\\d{4}\\s*[\\d:\\saApPmM]*';
// Regex to check for non-ISO date ranges that are separated by a dash (ISO is separated
//  differently because it uses dashes in the date format). This helps us identify if the input is
// potentially a non-ISO absolute date range and separate the two pieces, but does not attempt to
// fully parse out the parts of the date, which is better handled by our date utils.
const ABSOLUTE_DATE_RANGE_REGEX_V1 = new RegExp(
  `^\\s*(${ROUGH_DATE_REGEX_STR_V1})\\s*-\\s*(${ROUGH_DATE_REGEX_STR_V1})\\s*$`
);

/**
 * Attempts to parse and return absolute time range in the v1 Chronosphere format (MM/dd/yyyy hhss aa). If a date cannot
 * be parsed, it returns undefined.
 */
const maybeParseV1AbsoluteTimeRange = (input: string, timeZone?: string): AbsoluteTimeRange | undefined => {
  const dateRangeMatch = input.match(ABSOLUTE_DATE_RANGE_REGEX_V1);
  if (dateRangeMatch && dateRangeMatch[1] && dateRangeMatch[2]) {
    const [_, startStr, endStr] = dateRangeMatch;

    const fullDateTimeRange = {
      start: parseAbsoluteDate(startStr, DATE_TIME_FORMAT_12, timeZone),
      end: parseAbsoluteDate(endStr, DATE_TIME_FORMAT_12, timeZone),
    };
    if (absoluteTimeRangeHasValidDates(fullDateTimeRange)) {
      return fullDateTimeRange;
    }

    const shortDateTimeRange = {
      start: parseAbsoluteDate(startStr, DATE_TIME_FORMAT_12_SHORT_TIME, timeZone),
      end: parseAbsoluteDate(endStr, DATE_TIME_FORMAT_12_SHORT_TIME, timeZone),
    };
    if (absoluteTimeRangeHasValidDates(shortDateTimeRange)) {
      return shortDateTimeRange;
    }

    const datesOnly = {
      // Assume start and end of the day when only dates are provided.
      start: parseAbsoluteDate(`${startStr.trim()} 12:00:00 AM`, DATE_TIME_FORMAT_12, timeZone),
      end: parseAbsoluteDate(`${endStr.trim()} 11:59:59 PM`, DATE_TIME_FORMAT_12, timeZone),
    };

    if (absoluteTimeRangeHasValidDates(datesOnly)) {
      return datesOnly;
    }
  }
};

/**
 * Checks for a time zone in the date string. If so, we use that. Otherwise, we use the user's time
 * zone.
 */
function getV2DateTimeZone(inputTimeZoneStr: string | undefined, timeZone?: string) {
  if (!inputTimeZoneStr) {
    return timeZone;
  }

  const parsedTimeZoneOffset = getOffsetStrFromTimeZoneName(inputTimeZoneStr);
  if (parsedTimeZoneOffset) {
    return parsedTimeZoneOffset;
  }

  return timeZone;
}

// Rough regex that checks for the V2 Chronosphere date format enough to avoid some edge cases
// where someone enters an invalid date input that is still parseable to our parsing library and
// leads to weird results (e.g. entering a date with a 2-digit year instead of 4-digit). We still
// leave the majority of date parsing to our date parsing lib.
const ROUGH_DATE_REGEX_STR_V2 =
  '([a-zA-Z]{3}\\s*\\d{2}[,\\s]*\\d{4})' + // Date (MMM dd, yyyy)
  '(?:,?\\s*)' + // Optional comma or whitespace
  // Flexible time formats (12h or 24h)
  // - Matches "H", "HH", "H:MM", "HH:MM", "H:MM:SS", "HH:MM:SS"
  // - Allows optional whitespace and AM/PM (e.g., "9 AM", "12:30PM")
  '((?:[\\d]{1,2}:[\\d]{2}(?::[\\d]{2})?|[\\d]{1,2})(?:\\s*(?:AM|PM))?)?' +
  '(?:,?\\s*)' +
  // Optional timezone - patterns are tried in order:
  // 1. Timezone code WITH offset: GMT+7, UTC-5:30, PST+8, etc.
  // 2. Just an offset: -07:00, +05:30
  // 3. Timezone code WITHOUT offset: PST, EST, GMT, UTC (but NOT if followed by +/- and a digit)
  '(' +
  '[A-Z]{3,4}[+-]\\d{1,2}(?::\\d{2})?|' + // e.g., GMT-7:00, PST+8
  '[+-]\\d{1,2}(?::\\d{2})?|' + // e.g., -07:00, +5:30
  '[A-Z]{3,4}(?![+-]\\d)' + // e.g., UTC, PST (but not GMT from GMT-7)
  ')?';

// Time-only pattern for same-day ranges
const TIME_ONLY_STR = '((?:[\\d]{1,2}:[\\d]{2}(?::[\\d]{2})?|[\\d]{1,2})(?:\\s*(?:AM|PM))?)';

// Regex to check for non-ISO date ranges that are separated by a dash (ISO is separated
//  differently because it uses dashes in the date format). This helps us identify if the input is
// potentially a non-ISO absolute date range and separate the two pieces, but does not attempt to
// fully parse out the parts of the date, which is better handled by our date utils.
// The end can be either a full date+time OR just a time (for same-day ranges)
const ABSOLUTE_DATE_RANGE_REGEX_V2 = new RegExp(
  `^\\s*(${ROUGH_DATE_REGEX_STR_V2})\\s*-\\s*(?:(${ROUGH_DATE_REGEX_STR_V2})|${TIME_ONLY_STR})?\\s*$`,
  'i'
);

// Regex patterns for time string matching in formatParsedV2DateTime
const REGEX_HH_MM_AMPM = /^\d{1,2}:\d{2}(\s*[AP]M)?$/i;
const REGEX_HH_AMPM = /^\d{1,2}(\s*[AP]M)?$/i;
const REGEX_REPLACE_HH_MM_AMPM = /(\d{1,2}:\d{2})(\s*[AP]M)?/i;
const REGEX_REPLACE_HH_AMPM = /(\d{1,2})(\s*[AP]M)?/i;

/**
 * Takes the date-time information loosely parsed from the user input and attempt to format them in
 * the shape our date-time utils expect for V2. Enables us to support edge cases where users forget
 * commas and things like that.
 */
function formatParsedV2DateTime(dateString?: string, timeString?: string) {
  const [startMonth, startDay, startYear] = (dateString || '').split(/[\s,]+/);
  const formattedDate = `${startMonth} ${startDay}, ${startYear}`;
  let formattedTime = timeString || '';
  // If time does not have minutes or seconds, append ':00'
  if (formattedTime) {
    // Match HH:mm or HH:mm AM/PM
    if (REGEX_HH_MM_AMPM.test(formattedTime)) {
      formattedTime = formattedTime.replace(REGEX_REPLACE_HH_MM_AMPM, '$1:00$2');
    }
    // Match HH AM/PM
    else if (REGEX_HH_AMPM.test(formattedTime)) {
      formattedTime = formattedTime.replace(REGEX_REPLACE_HH_AMPM, '$1:00:00$2');
    }
  }
  const formattedDateTime = `${formattedDate}, ${formattedTime}`;

  return {
    formattedDate,
    formattedDateTime,
  };
}

/**
 * Attempts to parse and return absolute time range in the v2 Chronosphere format. If a date cannot
 * be parsed, it returns undefined.
 */
const maybeParseV2AbsoluteTimeRange = (input: string, timeZone?: string): AbsoluteTimeRange | undefined => {
  const dateRangeMatch = input.match(ABSOLUTE_DATE_RANGE_REGEX_V2);

  if (dateRangeMatch) {
    // Keeping unused var names to clarify groupings.
    const [
      _fullMatch,

      _fullStartStr,
      startDateStr,
      startTimeStr,
      startTimeZoneStr,

      _fullEndStr,
      endDateStr,
      endTimeStr,
      endTimeZoneStr,
      endTimeOnlyStr,
    ] = dateRangeMatch;

    // Determine actual end values based on what was matched
    const actualEndDateStr = endDateStr || startDateStr;
    const actualEndTimeStr = endTimeOnlyStr || endTimeStr || startTimeStr;
    const actualEndTimeZoneStr = endTimeZoneStr || startTimeZoneStr;

    // V2 chronosphere format can include time zones, so we need to account for them when formatting dates.
    let startTz = getV2DateTimeZone(startTimeZoneStr, timeZone);
    const endTz = getV2DateTimeZone(actualEndTimeZoneStr, timeZone);
    // if an end time zone is specified but a start time zone is not, use the end time zone for the start as well
    if (!startTz && endTz) {
      startTz = endTz;
    }

    // Process the parsed potential date/time information into a format the v2 date-time utils can process.
    const { formattedDate: formattedStartDate, formattedDateTime: formattedStartDateTime } = formatParsedV2DateTime(
      startDateStr,
      startTimeStr
    );
    const { formattedDate: formattedEndDate, formattedDateTime: formattedEndDateTime } = formatParsedV2DateTime(
      actualEndDateStr,
      actualEndTimeStr
    );

    const tryParseDateRange = (start: string, end: string, format: string): AbsoluteTimeRange | undefined => {
      const range = {
        start: parseAbsoluteDate(start, format, startTz),
        end: parseAbsoluteDate(end, format, endTz),
      };
      return absoluteTimeRangeHasValidDates(range) ? range : undefined;
    };

    const formats = [
      getDateTimeFormat({ timeFormat: '24h' }),
      getDateTimeFormat({ timeFormat: '12h' }),
      getDateTimeFormat({ timeFormat: '24h', omitSeconds: true }),
      getDateTimeFormat({ timeFormat: '12h', omitSeconds: true }),
    ];

    for (const format of formats) {
      const parsed = tryParseDateRange(formattedStartDateTime, formattedEndDateTime, format);
      if (parsed) return parsed;
    }

    const datesOnly = tryParseDateRange(
      `${formattedStartDate}, 00:00:00`, // Assume start of the day when only dates are provided.
      `${formattedEndDate}, 23:59:59`, // Assume end of the day when only dates are provided.
      getDateTimeFormat({ timeFormat: '24h' })
    );
    if (datesOnly) return datesOnly;
  }
};

/**
 * Attempts to parse and return absolute time range for a single day in the Chronosphere format (MMM dd yyyy, HHss)
 * (i.e. not ISO). If a date cannot be parsed, it returns undefined.
 */
const maybeParseSingleDateTimeRange = (input: string, timeZone?: string): AbsoluteTimeRange | undefined => {
  const absoluteRangeV1 = maybeParseV1AbsoluteTimeRange(`${input.trim()}-${input.trim()}`, timeZone);
  if (absoluteRangeV1) {
    return absoluteRangeV1;
  }

  const absoluteRangeV2 = maybeParseV2AbsoluteTimeRange(`${input.trim()}-${input.trim()}`, timeZone);
  if (absoluteRangeV2) {
    return absoluteRangeV2;
  }

  const isoRange = maybeParseISOTimeRange(`${input.trim()}/${input.trim()}`, timeZone);
  if (isoRange) {
    return isoRange;
  }
};

// Very naive regex to check for ISO date ranges that are separated by a
// forward slash (/). This helps us identify if the input is potentially an ISO date range and
// separate the two pieces, but DOES NOT attempt to parse out the parts of the date, which is
// better handled by our date utils.
// https://en.wikipedia.org/wiki/ISO_8601#Time_intervals
const ISO_DATE_RANGE_REGEX = /^([^/]+)\s*\/\s*([^/]+)$/;
// Regex to parse out the day, time (optional), and time zone in ISO format. This helps us fall
// back to specific times when a time is not set. It also helps us determine if we should respect
// the specfied time zone OR assume it is in the user's configured time zone.
// https://en.wikipedia.org/wiki/ISO_8601#Time_zone_designators
const ISO_DATE_PART_REGEX = /^([\d-]+)(T[\d:.]+)?(Z|([+-]\d{2}:?(\d{2})?))?$/;

/**
 * Parses an ISO absolute date in the specified time zone.
 */
const parseISODate = (dateStr: string, fallbackTime: string, timeZone?: string) => {
  const dateParts = dateStr.trim().match(ISO_DATE_PART_REGEX);
  if (!dateParts) {
    // If we cannot parse the date, something went wrong. Return an invalid date.
    return new Date(NaN);
  }

  const [_, isoDay, isoTime, isoTimeZone] = dateParts;

  // Construct a full ISO date time based on the combination of the passed values and fallbacks
  // for values not set (e.g. time).
  const updatedDateString = `${isoDay}${isoTime ?? fallbackTime}${isoTimeZone ?? ''}`;

  const date = new Date(updatedDateString);

  if (isoTimeZone) {
    // Do not adjust the input string to the user-specified time zone when the input time explicitly
    // defines a time zone via ISO time zone formats.
    return date;
  }

  // Adjust for the user-specified time zone because a time zone is not specified in the string.
  return parsedTimeToZonedTime(date, timeZone);
};

/**
 * Attempts to parse and return absolute time range in ISO format. If a date cannot be parsed, it
 * returns undefined.
 */
const maybeParseISOTimeRange = (input: string, timeZone?: string): AbsoluteTimeRange | undefined => {
  const dateRangeMatch = input.match(ISO_DATE_RANGE_REGEX);
  if (dateRangeMatch && dateRangeMatch[1] && dateRangeMatch[2]) {
    const [_, startStr, endStr] = dateRangeMatch;

    const timeRange = {
      start: parseISODate(startStr, 'T00:00:00', timeZone),
      end: parseISODate(endStr, 'T23:59:59', timeZone),
    };

    if (absoluteTimeRangeHasValidDates(timeRange)) {
      return timeRange;
    }
  }
};

// Very naive regex to check for a potential relative time range in one of these formats:
//   - Last N unit
//   - Past N unit
//   - N unit
// It does not attempt to check for all the potential units, which could lead to an overly complex
// and expensive regex. Those checks are handled with separate logic.
const DURATION_NATURAL_LANGUAGE_REGEX = /^\s*((?:\w+)\s+)?(\d+)\s*(\w+)\s*$/;
const DURATION_NATURAL_LANGUAGE_MAPPING: Record<string, (typeof DURATION_UNIT)[keyof typeof DURATION_UNIT]> = {
  // Set up custom name mappings we cannot programmatically infer from the DURATION_UNIT enum.

  sec: DURATION_UNIT.SECOND,
  secs: DURATION_UNIT.SECOND,

  min: DURATION_UNIT.MINUTE,
  mins: DURATION_UNIT.MINUTE,

  hr: DURATION_UNIT.HOUR,
  hrs: DURATION_UNIT.HOUR,

  wk: DURATION_UNIT.WEEK,
  wks: DURATION_UNIT.WEEK,

  mo: DURATION_UNIT.MONTH,
  mos: DURATION_UNIT.MONTH,
};

// Programmatically add common names, plurals, and shorthand units from DURATION_UNIT enum.
Object.entries(DURATION_UNIT).forEach(([key, value]) => {
  // Add shorthand (e.g. s for seconds, m for minutes)
  DURATION_NATURAL_LANGUAGE_MAPPING[value] = value;

  // Add singular name (e.g. second, minute)
  DURATION_NATURAL_LANGUAGE_MAPPING[key.toLowerCase()] = value;
  // Add pluralized name (e.g. seconds, minutes)
  DURATION_NATURAL_LANGUAGE_MAPPING[`${key.toLowerCase()}s`] = value;
});

// For now, these keywords are a map to booleans because we just want a quick lookup to validate,
// but all keywords are for "past duration."
const DURATION_NATURAL_LANGUAGE_KEYWORDS: Record<string, boolean> = {
  past: true,
  last: true,
};

/**
 * Attempts to parse and return a relative time range in Chronosphere format. If a time range cannot
 * be parsed, it returns undefined.
 */
const maybeParseRelativeTimeRange = (input: string): TimeRangeInputValue | undefined => {
  const dateRangeMatch = input.match(DURATION_NATURAL_LANGUAGE_REGEX);
  if (!dateRangeMatch) {
    return;
  }

  const [_, keyword, durationStr, unitStr] = dateRangeMatch;
  if (!!keyword && !DURATION_NATURAL_LANGUAGE_KEYWORDS[keyword.trim().toLowerCase()]) {
    // If keyword is not in the list of supported keywords AND is not empty, return because it is
    // not supported.
    return;
  }

  if (durationStr && unitStr) {
    // Check if the unit is valid as is or normalized to lowercase. We do the case-sensitive check
    // first to account for the difference between m (minutes) and M (months).
    const unit = DURATION_NATURAL_LANGUAGE_MAPPING[unitStr] || DURATION_NATURAL_LANGUAGE_MAPPING[unitStr.toLowerCase()];
    if (!unit) {
      return;
    }

    const duration = parseInt(durationStr, 10);
    // Check if the duration is valid.
    if (duration > 0) {
      return getTimeRangeFromDurationString(`${duration}${unit}`);
    }
  }
};

/**
 * Attempts to parse a time range from text. If a valid time range can be parsed, it is returned.
 * Otherwise, it returns `undefined`.
 */
export const parseTimeRange = (input: string, timeZone?: string): TimeRangeInputValue | undefined => {
  if (!input || !input.trim()) {
    return;
  }

  const absoluteTimeRangeV2 = maybeParseV2AbsoluteTimeRange(input, timeZone);
  if (absoluteTimeRangeV2) {
    return absoluteTimeRangeV2;
  }

  const absoluteTimeRangeV1 = maybeParseV1AbsoluteTimeRange(input, timeZone);
  if (absoluteTimeRangeV1) {
    return absoluteTimeRangeV1;
  }

  const isoTimeRange = maybeParseISOTimeRange(input, timeZone);
  if (isoTimeRange) {
    return isoTimeRange;
  }

  const relativeTime = maybeParseRelativeTimeRange(input);
  if (relativeTime) {
    return relativeTime;
  }

  const singleDateRange = maybeParseSingleDateTimeRange(input, timeZone);
  if (singleDateRange) {
    return singleDateRange;
  }
};
